import React, { useEffect, useState } from 'react'
import { DropdownSelect, Grid, InputLabel, Spinner, Tooltip, useTheme } from '@paintscout/ui'
import type { DropdownSelectOption } from '@paintscout/ui'
import { useCreatePaymentActivityMutation } from '@paintscout/api'
import type { PayrixTxnMetadata } from 'paintscout'
import { getPayrixCredentials, randomString } from '@paintscout/util/util'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'
import { usePayrixStyles } from './usePayrixStyles'
import type { FormProps } from './CollectPayrixPaymentDialog'

export default function ACHForm(props: FormProps) {
  const classes = usePayrixStyles()
  const [createPaymentActivity] = useCreatePaymentActivityMutation()
  const [accountType, setAccountType] = useState<DropdownSelectOption>({ label: 'Personal Checking', value: 8 })
  const theme = useTheme()
  const {
    amount,
    companyId,
    handlePaymentError,
    handleSuccessfulPayment,
    merchantId,
    note,
    options,
    payFieldsLoading,
    paymentRequestId,
    quote,
    setActivityId,
    setPayFieldsLoading,
    setSubmitting,
    source,
    surcharge
  } = props
  const { enqueueSnackbar } = useSnackbar()
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada'

  useEffect(() => {
    // Load the Payrix script
    let submitButton
    let handleSubmit
    let activityId
    const { publicKey, apiUrl, region } = getPayrixCredentials(options)

    const script = document.createElement('script')
    script.src = `${apiUrl}/payFieldsScript?spa=1`
    script.type = 'text/javascript'
    script.async = true
    script.onload = () => {
      const PayFields = ((window as any).PayFields as any) || {}
      PayFields.config.apiKey = publicKey
      PayFields.config.merchant = merchantId
      PayFields.config.mode = 'txn'
      PayFields.config.txnType = 'ecsale'
      PayFields.config.amount = Math.round(amount * 100)
      PayFields.customizations.optionalFields = ['#payment_cvv', '#expiration']
      PayFields.customizations.placeholders = {
        '#account_number': '123456789',
        '#routing': isCanadianPayrix ? '010156789' : '12345678',
        '#name': ''
      }
      PayFields.config.order = quote._id

      PayFields.fields = [
        // Number must be present for PayField divs to load
        {
          type: 'number',
          element: '#number'
        },
        {
          type: 'name',
          element: '#name',
          values: {
            name: quote.contact ? `${quote.contact.first_name} ${quote.contact.last_name}` : null
          }
        },
        {
          type: 'account_number',
          element: '#account_number'
        },
        {
          type: 'account_type',
          element: '#account_type'
        },
        {
          type: 'routing',
          element: '#routing'
        }
      ]
      PayFields.customizations.style = {
        '.number': {
          display: 'none'
        },
        '.input': {
          ...theme.typography.body1,
          border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
          borderRadius: theme.borderRadius.md,
          boxSizing: 'border-box',
          height: '40px',
          padding: '10px 16px',
          width: '100%',
          fontSize: '1rem',
          color: theme.palette.text.primary
        },
        '.card-icon': {
          marginRight: '6rem',
          marginTop: '.25rem'
        }
      }

      PayFields.ready()

      // Set setPayFieldsLoading false after 1 second to prevent flickering
      setTimeout(() => {
        setPayFieldsLoading(false)
      }, 1000)

      handleSubmit = () => {
        setSubmitting(true)
        setPayFieldsLoading(true)
        PayFields.customizations.optionalFields = ['#number']

        // create an optimistic activityId and create the activity on success
        activityId = randomString({ length: 32, charset: 'abcdefghijklmnopqrstuvwxyz0123456789' })

        // 90/100 characters already used
        const payrixMeta: PayrixTxnMetadata = {
          aId: activityId,
          cId: companyId,
          t: quote.is_invoice ? 'p' : 'd',
          env: process.env.DEPLOYMENT_STAGE
        }

        PayFields.config.additionalData = { txnDescription: JSON.stringify(payrixMeta) }

        PayFields.submit()
      }

      submitButton = document.querySelector('#payrix-submit-button')
      submitButton?.addEventListener('click', handleSubmit)

      // Failure Callback
      PayFields.onFailure = function (err) {
        console.log('[PayFields ACH] failure')
        setSubmitting(false)
        setPayFieldsLoading(false)
        const errors = (err.errors as any[]) || []
        console.log(JSON.stringify(errors, null, 2))
        const error15 = errors.find((error) => error.code === 15)
        if (error15) {
          if (error15.field === 'payment.number') {
            enqueueSnackbar('Please enter a valid account number', { variant: 'error' })
          } else if (error15.errorCode === 'txn_limit_exceeded') {
            enqueueSnackbar('Transaction limit exceeded. Please contact support.', { variant: 'error' })
          } else if (error15.msg) {
            enqueueSnackbar(error15.msg, { variant: 'error' })
            Sentry.captureMessage(err, { tags: { payrixCode15: true } })
          } else {
            // Error code 15 is a validation error that isn't caught by the onValidationFailure callback
            // So we'll just set submitting to false and hope the error message shows up in the PayFields div or is obvious to the user
            // We'll also capture the error in Sentry if its not handled by a snackbar above
            enqueueSnackbar('Validation error. Please make sure all fields are complete and correct.', {
              variant: 'error'
            })
            Sentry.captureException(err, { tags: { payrixCode15: true } })
          }
        } else {
          handlePaymentError(err)
        }
      }

      // Success Callback
      PayFields.onSuccess = async function (response) {
        const activity = await createPaymentActivity({
          variables: {
            ach: true,
            amount,
            deposit: !quote.is_invoice,
            docId: quote._id,
            id: activityId,
            note: note?.length > 100 ? `${note.substring(0, 100)}...` : note,
            paymentRequestId,
            provider: 'payrix',
            providerDetails: { payrix: { region } },
            source,
            isProcessing: true,
            surcharge
          }
        })

        const responseId = activity.data.createPaymentActivity.activity._id
        setActivityId(responseId)

        console.log(response)
        handleSuccessfulPayment()
      }

      PayFields.onValidationFailure = function () {
        console.log('onValidationFailure')
        setSubmitting(false)
        setPayFieldsLoading(false)
      }
    }

    document.head.appendChild(script)

    return () => {
      // cleanup
      const PayFields = ((window as any).PayFields as any) || {}
      submitButton.removeEventListener('click', handleSubmit)
      document.head.removeChild(script)
      PayFields.unmountAll()
      ;(window as any).PayFields = undefined
    }
  }, [])

  return (
    <>
      {payFieldsLoading && <Spinner style={{ display: 'block', margin: '81px auto', width: 30 }} />}
      <Grid container item spacing={2} className={classes.root} style={{ display: payFieldsLoading ? 'none' : 'flex' }}>
        <Grid item xs={12} md={6} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="name">
            Full Name
          </InputLabel>
          <div id="name"></div>
        </Grid>
        <Grid item xs={12} md={6} className={classes.payField}>
          <InputLabel className={classes.inputLabel} htmlFor="account_number">
            Account Number
          </InputLabel>
          <div id="account_number"></div>
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownSelect
            fullWidth
            label={'Account Type'}
            options={[
              { label: 'Personal Checking', value: 8 },
              { label: 'Personal Savings', value: 9 },
              { label: 'Corporate (Business) Checking', value: 10 },
              { label: 'Corporate (Business) Savings', value: 11 }
            ]}
            variant="single"
            value={accountType}
            onChange={setAccountType}
            popperModifiers={{
              flip: {
                enabled: false
              }
            }}
          />
          <input id="account_type" type="hidden" value={accountType.value} />
        </Grid>
        <Grid item xs={12} md={6} className={classes.payField}>
          <Tooltip
            classes={{ badge: classes.inputTooltipBadge }}
            content={
              isCanadianPayrix
                ? 'Your routing number starts with a 0, then your 3-digit institution number, and ends with your 5-digit transit number (e.g., institution 101 and transit 56789 ➞ 010156789)'
                : undefined
            }
            disabled={!isCanadianPayrix}
            hideIcon={!isCanadianPayrix}
          >
            <InputLabel className={classes.inputLabel} htmlFor="routing">
              Routing Number
            </InputLabel>
          </Tooltip>
          <div id="routing"></div>
        </Grid>
        <div style={{ display: 'none' }}>
          <div id="number"></div> {/* Number must be present for PayField divs to load */}
        </div>
      </Grid>
    </>
  )
}
